import { adminService } from '@/api/services';
import type { UnwrapData } from '@/api/types';

import type {
  IAdmissionApplicationsResponse,
  EApplicationStatus,
  IAdmissionApplicationResponse,
  IMoveToNextStageResponse,
  ISendNotificationsResponse,
} from './types';

export const admissionApplicationService = adminService.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Получить список заявок панели отбора
     */
    getAdmissionApplications: builder.query<
      IAdmissionApplicationsResponse,
      { schoolId: number; page?: number; perPage?: number; statusFilter?: EApplicationStatus[] | null }
    >({
      query: ({ schoolId, page, perPage, statusFilter }) => ({
        method: 'GET',
        url: `/schools/${schoolId}/admission_applications`,
        params: {
          page,
          per_page: perPage,
          q: {
            status_in: statusFilter,
          },
        },
      }),
      providesTags: (_, __, { schoolId }) => [{ type: 'SchoolAdmissionApplications', id: schoolId }],
    }),
    /**
     * Получить конкретную заявку панели отбора
     */
    getAdmissionApplication: builder.query<
      UnwrapData<IAdmissionApplicationResponse>,
      { schoolId: number; applicationId: number }
    >({
      query: ({ schoolId, applicationId }) => ({
        method: 'GET',
        url: `/schools/${schoolId}/admission_applications/${applicationId}`,
      }),
      providesTags: (_, __, { schoolId, applicationId }) => [
        { type: 'SchoolAdmissionApplication', id: `${schoolId}-${applicationId}` },
      ],
      transformResponse: (response: IAdmissionApplicationResponse): UnwrapData<IAdmissionApplicationResponse> =>
        response.data,
    }),
    /**
     * Одобрить заявку панели отбора
     */
    approveAdmissionApplication: builder.mutation<
      IAdmissionApplicationResponse,
      { schoolId: number; applicationId: number }
    >({
      query: ({ schoolId, applicationId }) => ({
        method: 'POST',
        url: `/schools/${schoolId}/admission_applications/${applicationId}/approve`,
      }),
      invalidatesTags: (_, __, { schoolId, applicationId }) => [
        { type: 'SchoolAdmissionApplications', id: schoolId },
        { type: 'SchoolAdmissionApplication', id: `${schoolId}-${applicationId}` },
        { type: 'SchoolAdmissionSurvey', id: schoolId },
      ],
    }),
    /**
     * Отклонить заявку заявку панели отбора
     */
    rejectAdmissionApplication: builder.mutation<
      IAdmissionApplicationResponse,
      { schoolId: number; applicationId: number; rejectionReason: string }
    >({
      query: ({ schoolId, applicationId, rejectionReason }) => ({
        method: 'POST',
        url: `/schools/${schoolId}/admission_applications/${applicationId}/reject`,
        body: {
          data: {
            rejection_reason: rejectionReason,
          },
        },
      }),
      invalidatesTags: (_, __, { schoolId, applicationId }) => [
        { type: 'SchoolAdmissionApplications', id: schoolId },
        { type: 'SchoolAdmissionApplication', id: `${schoolId}-${applicationId}` },
        { type: 'SchoolAdmissionSurvey', id: schoolId },
      ],
    }),
    /**
     * Отправить уведомления о принятом решении по заявке
     */
    sendNotifications: builder.mutation<ISendNotificationsResponse, { schoolId: number }>({
      query: ({ schoolId }) => ({
        method: 'POST',
        url: `/schools/${schoolId}/admission_flow/send_notifications`,
      }),
      invalidatesTags: (_, __, { schoolId }) => [{ type: 'SchoolAdmissionSurvey', id: schoolId }],
    }),
    /**
     * Перевести одобренные заявки на следующий этап
     */
    moveToNextStage: builder.mutation<IMoveToNextStageResponse, { schoolId: number }>({
      query: ({ schoolId }) => ({
        method: 'POST',
        url: `/schools/${schoolId}/admission_flow/transfer_to_testing`,
      }),
      invalidatesTags: (_, __, { schoolId }) => [{ type: 'SchoolAdmissionSurvey', id: schoolId }],
    }),
  }),
});

export const {
  useGetAdmissionApplicationsQuery,
  useLazyGetAdmissionApplicationsQuery,
  useGetAdmissionApplicationQuery,
  useApproveAdmissionApplicationMutation,
  useRejectAdmissionApplicationMutation,
  useSendNotificationsMutation,
  useMoveToNextStageMutation,
} = admissionApplicationService;
